import { FeatureModule, FeatureModuleSave, InternalPackage, InternalRole, PackageCreate, PackageTier, PackageType, RoleCreate } from '../../types/Plans'

export const planTabs = ['packages', 'roles', 'modules'] as const
export type PlanTab = (typeof planTabs)[number]

export const packageTypes = [PackageType.None, PackageType.Insights, PackageType.MyGames, PackageType.Markets]
export const packageTiers = [
  PackageTier.Free,
  PackageTier.Indie,
  PackageTier.Basic,
  PackageTier.Pro,
  PackageTier.Enterprise,
  PackageTier.Tailored,
  PackageTier.Trial,
]

export const packageTypeLabelKeys: Record<PackageType, string> = {
  [PackageType.None]: 'internal-csm:plans_type_none',
  [PackageType.Insights]: 'internal-csm:plans_type_insights',
  [PackageType.MyGames]: 'internal-csm:plans_type_my_games',
  [PackageType.Markets]: 'internal-csm:plans_type_markets',
}

export const packageTierLabelKeys: Record<PackageTier, string> = {
  [PackageTier.Free]: 'internal-csm:plans_tier_free',
  [PackageTier.Indie]: 'internal-csm:plans_tier_indie',
  [PackageTier.Basic]: 'internal-csm:plans_tier_basic',
  [PackageTier.Pro]: 'internal-csm:plans_tier_pro',
  [PackageTier.Enterprise]: 'internal-csm:plans_tier_enterprise',
  [PackageTier.Tailored]: 'internal-csm:plans_tier_tailored',
  [PackageTier.Trial]: 'internal-csm:plans_tier_trial',
}

/** Role names in code-unit order, like legacy's lodash `sortBy`. */
export const sortRoleNames = (roles?: string[] | null) => [...(roles ?? [])].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))

export const formatRoleNames = (roles?: string[] | null) => sortRoleNames(roles).join(', ')

const isSameList = (a: string[], b: string[]) => a.length === b.length && a.every((value, index) => value === b[index])
const isSameSet = (a?: string[] | null, b?: string[] | null) => isSameList(sortRoleNames(a), sortRoleNames(b))

/** Appends the roles the list doesn't have yet, keeping the existing order. */
export const addRoleNames = (current: string[], added: string[]) => [
  ...current,
  ...added.filter((role, index) => !current.includes(role) && added.indexOf(role) === index),
]

/** An unchanged text keeps its stored value (`null` included); a cleared one is stored as `null`. */
const toNullableText = (value: string, stored?: string | null) => {
  if (value === (stored ?? '')) return stored ?? null
  return value.trim() ? value.trim() : null
}

const sameText = (a?: string | null, b?: string | null) => (a ?? '') === (b ?? '')

export type NameError = 'required' | 'taken' | 'invalid'

const getNameError = (name: string, takenNames: string[]): NameError | undefined => {
  const trimmed = name.trim()
  if (!trimmed) return 'required'
  if (takenNames.includes(trimmed)) return 'taken'
  return undefined
}

export type WholeNumberError = 'required' | 'invalid'

/** The backend stores whole numbers only: it drops the decimals of `12.5`. */
export const parseWholeNumber = (value: string) => (/^\d+$/.test(value.trim()) ? Number(value.trim()) : undefined)

export const getWholeNumberError = (value: string): WholeNumberError | undefined => {
  if (!value.trim()) return 'required'
  return parseWholeNumber(value) === undefined ? 'invalid' : undefined
}

export type PackageRoleFilter = 'with' | 'without'

export const filterPackages = (packages: InternalPackage[], role: string | undefined, filter: PackageRoleFilter) =>
  role ? packages.filter((planPackage) => (planPackage.roles ?? []).includes(role) === (filter === 'with')) : packages

export type PackageFormValues = {
  name: string
  description: string
  type: PackageType
  tier: PackageTier
  price: string
  maxGameSlots: string
  maxFollowedGames: string
  visible: boolean
  roles: string[]
}

export const toPackageFormValues = (planPackage?: InternalPackage): PackageFormValues => ({
  name: planPackage?.name ?? '',
  description: planPackage?.description ?? '',
  type: planPackage?.type ?? PackageType.None,
  tier: planPackage?.tier ?? PackageTier.Free,
  price: String(planPackage?.price ?? 0),
  maxGameSlots: String(planPackage?.maxGameSlots ?? 0),
  maxFollowedGames: String(planPackage?.maxFollowedGames ?? 0),
  visible: planPackage?.visible ?? false,
  roles: planPackage?.roles ?? [],
})

export const getPackageNameError = (name: string, packages: InternalPackage[], editedId?: string) =>
  getNameError(
    name,
    packages.filter(({ id }) => id !== editedId).map((planPackage) => planPackage.name)
  )

/** Legacy's create body: the fields of the dialog, nothing else. */
export const toPackageCreate = (values: PackageFormValues): PackageCreate => ({
  roles: values.roles,
  name: values.name.trim(),
  description: toNullableText(values.description),
  price: parseWholeNumber(values.price) ?? 0,
  maxGameSlots: parseWholeNumber(values.maxGameSlots) ?? 0,
  maxFollowedGames: parseWholeNumber(values.maxFollowedGames) ?? 0,
  tier: values.tier,
  type: values.type,
  visible: values.visible,
})

/** Legacy's update body: the complete stored package with the dialog's fields applied. Unchanged values are sent exactly as stored. */
export const toPackageUpdate = (stored: InternalPackage, values: PackageFormValues): InternalPackage => ({
  ...stored,
  name: values.name === stored.name ? stored.name : values.name.trim(),
  description: toNullableText(values.description, stored.description),
  roles: isSameList(values.roles, stored.roles ?? []) ? stored.roles : values.roles,
  price: parseWholeNumber(values.price) ?? stored.price,
  maxGameSlots: parseWholeNumber(values.maxGameSlots) ?? stored.maxGameSlots,
  maxFollowedGames: parseWholeNumber(values.maxFollowedGames) ?? stored.maxFollowedGames,
  tier: values.tier,
  type: values.type,
  visible: values.visible,
})

export type PackageField = 'name' | 'description' | 'roles' | 'type' | 'tier' | 'price' | 'maxGameSlots' | 'maxFollowedGames' | 'visible'

export const packageFieldLabelKeys: Record<PackageField, string> = {
  name: 'internal-csm:plans_package_name',
  description: 'internal-csm:plans_description',
  roles: 'internal-csm:plans_roles',
  type: 'internal-csm:plans_type',
  tier: 'internal-csm:plans_tier',
  price: 'internal-csm:plans_price',
  maxGameSlots: 'internal-csm:plans_game_slots',
  maxFollowedGames: 'internal-csm:plans_followed_games',
  visible: 'internal-csm:plans_visible',
}

export const getUnstoredPackageFields = (sent: PackageCreate, stored: InternalPackage): PackageField[] => {
  const fields: PackageField[] = []
  if (sent.name !== stored.name) fields.push('name')
  if (!sameText(sent.description, stored.description)) fields.push('description')
  if (!isSameSet(sent.roles, stored.roles)) fields.push('roles')
  if (sent.type !== stored.type) fields.push('type')
  if (sent.tier !== stored.tier) fields.push('tier')
  if (sent.price !== stored.price) fields.push('price')
  if (sent.maxGameSlots !== stored.maxGameSlots) fields.push('maxGameSlots')
  if (sent.maxFollowedGames !== stored.maxFollowedGames) fields.push('maxFollowedGames')
  if (!!sent.visible !== !!stored.visible) fields.push('visible')
  return fields
}

export type RoleFormValues = { name: string; description: string }

export const toRoleFormValues = (role?: InternalRole): RoleFormValues => ({ name: role?.name ?? '', description: role?.description ?? '' })

/** The name is a URL segment of `DELETE roles/{name}` and what the app checks access with, so it can't hold spaces or URL characters. */
export const getRoleNameError = (name: string, roles: InternalRole[]): NameError | undefined => {
  const error = getNameError(
    name,
    roles.map((role) => role.name)
  )
  if (error) return error
  return /[\s/?#%\\]/.test(name.trim()) ? 'invalid' : undefined
}

export const toRoleCreate = (values: RoleFormValues): RoleCreate => ({ name: values.name.trim(), description: toNullableText(values.description) })

export const toRoleUpdate = (stored: InternalRole, values: RoleFormValues): InternalRole => ({
  ...stored,
  description: toNullableText(values.description, stored.description),
})

export const isRoleDescriptionStored = (sent: RoleCreate, stored: InternalRole) => sameText(sent.description, stored.description)

export type RoleUsage = { packages: string[]; modules: string[] }

export const getRoleUsage = (roleName: string, packages: InternalPackage[], modules: FeatureModule[]): RoleUsage => ({
  packages: packages
    .filter((planPackage) => (planPackage.roles ?? []).includes(roleName))
    .map(({ name }) => name)
    .sort((a, b) => a.localeCompare(b)),
  modules: modules
    .filter((featureModule) => featureModule.roles.includes(roleName))
    .map(({ name }) => name)
    .sort((a, b) => a.localeCompare(b)),
})

export type ModuleFormValues = { name: string; description: string; roles: string[] }

export const toModuleFormValues = (featureModule?: FeatureModule): ModuleFormValues => ({
  name: featureModule?.name ?? '',
  description: featureModule?.description ?? '',
  roles: featureModule?.roles ?? [],
})

export const getModuleNameError = (name: string, modules: FeatureModule[], editedId?: string) =>
  getNameError(
    name,
    modules.filter(({ id }) => id !== editedId).map((featureModule) => featureModule.name)
  )

/** Legacy's bodies: `{ roles, name, description }` to create, the complete stored module with the dialog's fields to update. */
export const toModuleSave = (stored: FeatureModule | undefined, values: ModuleFormValues): FeatureModuleSave => {
  if (!stored) return { roles: values.roles, name: values.name.trim(), description: toNullableText(values.description) }
  return {
    ...stored,
    name: values.name === stored.name ? stored.name : values.name.trim(),
    description: toNullableText(values.description, stored.description),
    roles: isSameList(values.roles, stored.roles) ? stored.roles : values.roles,
  }
}

export type ModuleField = 'name' | 'description' | 'roles'

export const moduleFieldLabelKeys: Record<ModuleField, string> = {
  name: 'internal-csm:plans_module_name',
  description: 'internal-csm:plans_description',
  roles: 'internal-csm:plans_roles',
}

export const getUnstoredModuleFields = (sent: FeatureModuleSave, stored: FeatureModule): ModuleField[] => {
  const fields: ModuleField[] = []
  if (sent.name !== stored.name) fields.push('name')
  if (!sameText(sent.description, stored.description)) fields.push('description')
  if (!isSameSet(sent.roles, stored.roles)) fields.push('roles')
  return fields
}
